'use client'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import PhoneIcon from '@mui/icons-material/Phone'
import MailIcon from '@mui/icons-material/Mail'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import DeleteIcon from '@mui/icons-material/Delete'
import { useState, FormEvent, useEffect, useRef } from 'react'
import Link from 'next/link'
import LazyReCaptchaProvider, { useLazyReCaptcha } from '@/components/Layouts/LazyReCaptcha'

const inputStyle = {
    fontWeight: 900,
    fontStyle: 'italic',
    fontSize: 'clamp(12px, 1.5vw, 18px)',
    textTransform: 'uppercase',
    fontFamily: 'MonaSansCondensed, sans-serif',
    '& .MuiInputBase-input': {
        color: 'white',
    },
    '& .MuiInputLabel-root': {
        color: 'white',
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: 'white',
            borderRadius: '15px',
        },
        '&:hover fieldset': {
            borderColor: 'white',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'white',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'white',
        },
    },
}

const selectStyle = {
    ...inputStyle,
    '& .MuiSelect-icon': { color: 'white' },
}

interface ServiceOption {
    value: string
    label: string
}

interface FormField {
    label?: string
}

interface FormData {
    badge?: string
    highlight?: string
    title?: string
    name?: FormField
    email?: FormField
    phone?: FormField
    message?: FormField
    submit?: FormField
    attachFilesButtonText?: string
    service?: {
        label?: string
        options?: ServiceOption[]
    }
}

interface ContactData {
    title?: string
    phoneLabel?: string
    phone?: string
    emailLabel?: string
    email?: string
    areaLabel?: string
    area?: string
    whyTitle?: string
}

interface Content {
    title?: string
    subtitle?: string
    badge?: string
    service?: {
        label?: string
        options?: ServiceOption[]
    }
    form?: FormData
    contact?: ContactData
    whyChoose?: Array<{ item?: string } | string>
}

interface ContactUsProps {
    content?: Content
    reCaptchaSiteKey?: string
    blockName?: string
    tenantId?: string
}

const defaultContent: Content = {
    title: 'Ready for Your',
    subtitle: 'Contact us today for professional concrete cutting services in Toronto',
    form: {
        badge: 'Get Started',
        highlight: 'Free Quote',
        title: 'Get Your Free Quote',
        name: { label: 'Name' },
        email: { label: 'E-mail' },
        phone: { label: 'Phone number' },
        service: {
            label: 'Select Service',
            options: [
                { value: 'concrete-cutting', label: 'Concrete Cutting' },
                { value: 'wall-sawing', label: 'Wall Sawing' },
                { value: 'core-drilling', label: 'Core Drilling' },
                { value: 'wire-sawing', label: 'Wire Sawing' },
                { value: 'slab-sawing', label: 'Slab Sawing' },
                { value: 'bank-vault', label: 'Bank Vault Demolition' },
            ],
        },
        message: { label: 'Project Details' },
        submit: { label: 'Get Free Quote' },
    },
    contact: {
        title: 'Contact Information',
        phoneLabel: '7:00 AM – 8:00 PM Monday to Friday',
        phone: '(647) 558-8505',
        emailLabel: 'Email Us',
        email: 'info@drmcutting.com',
        areaLabel: 'Service Area',
        area: 'Toronto & GTA (500 km radius)',
        whyTitle: 'Why Choose Toronto Concrete?',
    },
    whyChoose: [
        'Licensed & Insured',
        '15+ Years Experience',
        'Modern Equipment',
        '24/7 Emergency Service',
        '100% Satisfaction Guarantee',
    ],
}

const Index = (props: ContactUsProps) => {
    // Получаем siteKey из пропсов или переменной окружения
    const reCaptchaSiteKeyFromProps = props.reCaptchaSiteKey
    const reCaptchaSiteKeyFromEnv = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
    const reCaptchaSiteKey = (reCaptchaSiteKeyFromProps || reCaptchaSiteKeyFromEnv || '').trim()
    
    const { content = defaultContent, tenantId = '' } = props
    const { title, subtitle, badge, service, contact = {}, whyChoose = [] } = content

    const form: FormData = {
        badge: badge || content.form?.badge,
        name: {
            label: 'Name',
        },
        email: {
            label: 'E-mail',
        },
        phone: {
            label: 'Phone number',
        },
        message: {
            label: 'What do you need?',
        },
        submit: {
            label: 'Submit',
        },
        attachFilesButtonText: content.form?.attachFilesButtonText || 'Attach Project Files',
        service: service || content.form?.service || {
            label: 'Select Service',
            options: [
                {
                    value: 'concrete-cutting',
                    label: 'Concrete Cutting',
                },
                {
                    value: 'wall-sawing',
                    label: 'Wall Sawing',
                },
                {
                    value: 'core-drilling',
                    label: 'Core Drilling',
                },
                {
                    value: 'wire-sawing',
                    label: 'Wire Sawing',
                },
                {
                    value: 'slab-sawing',
                    label: 'Slab Sawing',
                },
                {
                    value: 'bank-vault',
                    label: 'Bank Vault Demolition',
                },
            ],
        },
    }

    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
    const [errorMsg, setErrorMsg] = useState('')
    // Внутренний компонент формы с reCAPTCHA
    const ContactForm = () => {
        const { executeRecaptcha, loaded } = useLazyReCaptcha()
        const [selectedFiles, setSelectedFiles] = useState<File[]>([])
        const fileInputRef = useRef<HTMLInputElement>(null)
        // Используем reCaptchaSiteKey из замыкания родительского компонента
        // Если reCaptchaSiteKey пустой, пробуем получить из переменной окружения
        const siteKey = (reCaptchaSiteKey || process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '').trim()
        const [isRecaptchaReady, setIsRecaptchaReady] = useState(loaded)
        
        // Отслеживаем готовность reCAPTCHA
        useEffect(() => {
            if (loaded) {
                setIsRecaptchaReady(true)
                return
            }
            
            // Проверяем window.grecaptcha напрямую
            if (typeof window !== 'undefined' && window.grecaptcha) {
                setIsRecaptchaReady(true)
                return
            }
            
            // Периодически проверяем готовность
            const checkInterval = setInterval(() => {
                if (typeof window !== 'undefined' && window.grecaptcha) {
                    setIsRecaptchaReady(true)
                    clearInterval(checkInterval)
                }
            }, 200)
            
            return () => clearInterval(checkInterval)
        }, [loaded])

        const validateFile = (file: File): boolean => {
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
            const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png']
            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
            
            return allowedTypes.includes(file.type) && allowedExtensions.includes(fileExtension)
        }

        const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files || [])
            const validFiles: File[] = []
            const invalidFiles: string[] = []

            files.forEach(file => {
                if (validateFile(file)) {
                    validFiles.push(file)
                } else {
                    invalidFiles.push(file.name)
                }
            })

            if (invalidFiles.length > 0) {
                setErrorMsg(`Invalid file types: ${invalidFiles.join(', ')}. Only PDF, JPG, and PNG files are allowed.`)
                setTimeout(() => setErrorMsg(''), 5000)
            }

            if (validFiles.length > 0) {
                setSelectedFiles(prev => [...prev, ...validFiles])
            }

            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }

        const handleRemoveFile = (index: number) => {
            setSelectedFiles(prev => prev.filter((_, i) => i !== index))
        }

        const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault()
            // Keep a stable reference to the form element BEFORE any awaits
            const formEl = e.currentTarget
            if (!(formEl instanceof HTMLFormElement)) {
                setStatus('error')
                setErrorMsg('Form element not found')
                return
            }
            setStatus('loading')
            setErrorMsg('')

            try {
                // reCAPTCHA v3
                let recaptchaToken: string | undefined = undefined
                
                if (siteKey) {
                    // Если есть siteKey, используем reCAPTCHA
                    if (!executeRecaptcha) {
                        console.error('executeRecaptcha is not available')
                        setStatus('error')
                        setErrorMsg('reCAPTCHA is loading. Please wait a moment and try again.')
                        return
                    }
                    
                    try {
                        recaptchaToken = await executeRecaptcha('contactForm')
                        if (!recaptchaToken) {
                            setStatus('error')
                            setErrorMsg('Please verify you are not a robot')
                            return
                        }
                    } catch (err) {
                        console.error('reCAPTCHA execution error:', err)
                        setStatus('error')
                        const errorMessage = err instanceof Error ? err.message : 'Failed to verify reCAPTCHA'
                        setErrorMsg(errorMessage)
                        return
                    }
                }

                const fd = new FormData(formEl)
                
                // Add files to FormData
                selectedFiles.forEach((file) => {
                    fd.append(`files`, file)
                })
                
                // Add tenant and recaptcha token as form fields
                fd.append('tenant', tenantId)
                // Если siteKey есть, токен должен быть обязательно
                if (siteKey) {
                    if (!recaptchaToken) {
                        setStatus('error')
                        setErrorMsg('reCAPTCHA verification is required')
                        return
                    }
                    fd.append('recaptchaToken', recaptchaToken)
                }
                
                const res = await fetch('/api/email', {
                    method: 'POST',
                    headers: {
                        'x-api-secret': process.env.NEXT_PUBLIC_INTERNAL_API_SECRET ?? '',
                    },
                    body: fd,
                })

                if (!res.ok) {
                    const data = await res.json().catch(() => ({})) as { error?: string }
                    throw new Error(data.error || `Request failed with ${res.status}`)
                }

                setStatus('success')
                setSelectedFiles([])
            } catch (err) {
                setStatus('error')
                setErrorMsg(err instanceof Error ? err.message : 'Something went wrong')
            }
        }

        return (
            <Box
                component='form'
                onSubmit={handleSubmit}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
                <Grid
                    container
                    spacing={2}
                >
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            label={form.name?.label}
                            name='name'
                            type='text'
                            fullWidth
                            required
                            sx={{ ...inputStyle }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            label={form.email?.label}
                            name='email'
                            type='email'
                            fullWidth
                            required
                            sx={{ ...inputStyle }}
                        />
                    </Grid>
                </Grid>

                <Grid
                    container
                    spacing={2}
                >
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            label={form.phone?.label}
                            name='phone'
                            type='tel'
                            fullWidth
                            required
                            sx={{ ...inputStyle }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <FormControl
                            fullWidth
                            sx={{ ...selectStyle }}
                        >
                            <InputLabel id='service-label'>{form.service?.label}</InputLabel>
                            <Select
                                labelId='service-label'
                                label={form.service?.label}
                                name='service'
                                defaultValue=''
                            >
                                {form.service?.options?.map((opt) => (
                                    <MenuItem
                                        key={opt.value}
                                        value={opt.value}
                                    >
                                        {opt.label}
                                    </MenuItem>
                                )) || []}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <TextField
                    label={form.message?.label}
                    name='message'
                    multiline
                    rows={4}
                    fullWidth
                    required
                    sx={{ ...inputStyle }}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <input
                        ref={fileInputRef}
                        type='file'
                        accept='.pdf,.jpg,.jpeg,.png'
                        multiple
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    <Button
                        variant='outlined'
                        startIcon={<AttachFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                            ...inputStyle,
                            borderColor: 'white',
                            color: 'white',
                            '&:hover': {
                                borderColor: 'white',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            },
                        }}
                    >
                        {form.attachFilesButtonText || 'Attach Project Files'}
                    </Button>
                    {selectedFiles.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                            {selectedFiles.map((file, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: 1,
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography
                                        variant='body2'
                                        sx={{ fontSize: '0.875rem', flex: 1 }}
                                    >
                                        {file.name}
                                    </Typography>
                                    <Button
                                        size='small'
                                        onClick={() => handleRemoveFile(index)}
                                        sx={{ minWidth: 'auto', p: 0.5, color: 'white' }}
                                    >
                                        <DeleteIcon fontSize='small' />
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                <Button
                    variant='contained'
                    color='primary'
                    type='submit'
                    disabled={status === 'loading' || (Boolean(siteKey) && !isRecaptchaReady)}
                    sx={{ py: 1.5, fontWeight: 900 }}
                >
                    {status === 'loading' ? 'Sending…' : form.submit?.label || 'Submit'}{' '}
                    <ArrowRightAltIcon sx={{ ml: 1 }} />
                </Button>
                {status === 'error' && (
                    <Typography
                        variant='body2'
                        sx={{ mt: 1, color: 'error.main' }}
                    >
                        {errorMsg}
                    </Typography>
                )}
            </Box>
        )
    }

    return (
        <Box
            component='section'
            aria-label='Contact Us'
            
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: 4,
                background: 'black',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                flexGrow: 1,
                py: { xs: 6, md: 10 },
                px: 4,
                color: 'white',
                scrollMarginTop: { xs: '80px', md: '120px' },
            }}
        >
            {/* Header */}
            <Box sx={{ textAlign: 'center' }}>
                {badge && (
                    <Chip
                        label={badge}
                        sx={{
                            mb: 2,
                            color: 'white',
                            backgroundColor: 'var(--primary-color)',
                        }}
                    />
                )}
                <Typography
                    variant='h2'
                    sx={{ fontWeight: 900 }}
                >
                    {title}{' '}
                    <Box
                        component='span'
                        sx={{ color: 'var(--primary-color)' }}
                    >
                        {form.highlight || 'Free Quote'}
                    </Box>
                </Typography>
                <Typography
                    variant='body1'
                    sx={{ mt: 1, opacity: 0.85 }}
                >
                    {subtitle}
                </Typography>
            </Box>

            {/* Two-column layout */}
            <Grid
                container
                spacing={6}
                sx={{ width: { xs: '95vw', md: '85vw', lg: '70vw' } }}
            >
                {/* Left: Form */}
                <Grid size={{ xs: 12, lg: 6 }}>
                    <Typography
                        variant='body2'
                        sx={{ fontWeight: 800, mb: 2, fontSize: '1.6rem' }}
                    >
                        {form.title || 'Get Your Free Quote'}
                    </Typography>
                    {status === 'success' ? (
                        <Typography
                            variant='body1'
                            color='success'
                            sx={{ mb: 2 }}
                        >
                            Thank you for your submission! We will get back to you soon.
                        </Typography>
                    ) : reCaptchaSiteKey ? (
                        <LazyReCaptchaProvider siteKey={reCaptchaSiteKey}>
                            <ContactForm />
                        </LazyReCaptchaProvider>
                    ) : (
                        <ContactForm />
                    )}
                </Grid>

                {/* Right: Contact info + Why Choose */}
                <Grid size={{ xs: 12, lg: 6 }}>
                    <Box sx={{ mb: 4 }}>
                        <Typography
                            variant='body2'
                            sx={{ fontWeight: 800, mb: 2, fontSize: '1.6rem' }}
                        >
                            {contact.title || 'Contact Information'}
                        </Typography>
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        backgroundColor: 'primary.main',
                                        p: 1.5,
                                        borderRadius: 2,
                                    }}
                                >
                                    <PhoneIcon />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 700 }}>{contact.phoneLabel || 'Phone'}</Typography>
                                    <Typography
                                        component={Link}
                                        href={`tel:${'(647) 558-8505'}`}
                                        variant='body2'
                                        sx={{ color: 'rgb(248,113,113)' }}
                                    >
                                        {contact.phone || '(647) 558-8505'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        backgroundColor: 'primary.main',
                                        p: 1.5,
                                        borderRadius: 2,
                                    }}
                                >
                                    <MailIcon />
                                </Box>
                                <Link
                                    href={`mailto:${contact.email || 'info@drmcutting.com'}`}
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    <Box>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {contact.emailLabel || 'Email Us'}
                                        </Typography>
                                        <Typography sx={{ opacity: 0.85 }}>
                                            {contact.email || 'info@drmcutting.com'}
                                        </Typography>
                                    </Box>
                                </Link>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        backgroundColor: 'primary.main',
                                        p: 1.5,
                                        borderRadius: 2,
                                    }}
                                >
                                    <LocationOnIcon />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 700 }}>
                                        {contact.areaLabel || 'Service Area'}
                                    </Typography>
                                    <Typography sx={{ opacity: 0.85 }}>
                                        {contact.area || 'Toronto & GTA (500 km radius)'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box>
                        <Typography
                            variant='body2'
                            sx={{ fontWeight: 800, mb: 2, fontSize: '1.6rem' }}
                        >
                            {contact.whyTitle || 'Why Choose DRM?'}
                        </Typography>
                        <List dense>
                            {(whyChoose.length ? whyChoose : defaultContent.whyChoose || []).map((item, index) => {
                                const itemText = typeof item === 'string' ? item : item?.item || ''
                                return (
                                    <ListItem
                                        key={itemText || index}
                                        sx={{ px: 0, py: 0 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            <CheckCircleIcon
                                                color='success'
                                                fontSize='small'
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            slotProps={{ primary: { sx: { opacity: 0.9 } } }}
                                            primary={itemText}
                                        />
                                    </ListItem>
                                )
                            })}
                        </List>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}

export default Index

